import React from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Heading,
  Link,
  List,
  BlockQuote,
  Indent,
  IndentBlock,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageUpload,
  Base64UploadAdapter,
  Table,
  TableToolbar,
  Undo,
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

/**
 * WYSIWYG rich-text editor used for long-form fields (e.g. a blog post's
 * "content" body) wherever `ContentEditor` renders a `richtext` field.
 * Uses CKEditor 5's self-hosted npm distribution under the GPL license
 * (see https://ckeditor.com/docs/ckeditor5/latest/getting-started/licensing/license-key-and-activation.html) —
 * this is free for open-source/GPL-compliant use and shows a small
 * "Powered by CKEditor" badge in the editor footer.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  return (
    <div className="rich-text-editor border border-nova-border rounded-lg overflow-hidden [&_.ck-editor__editable]:min-h-[220px] [&_.ck-editor__editable]:!bg-white [&_.ck-editor__editable]:!text-gray-900 [&_.ck-toolbar]:!bg-nova-bg2 [&_.ck-toolbar]:!border-nova-border">
      <CKEditor
        editor={ClassicEditor}
        data={value || ''}
        config={{
          licenseKey: 'GPL',
          plugins: [
            Essentials,
            Paragraph,
            Bold,
            Italic,
            Underline,
            Heading,
            Link,
            List,
            BlockQuote,
            Indent,
            IndentBlock,
            Image,
            ImageToolbar,
            ImageCaption,
            ImageStyle,
            ImageUpload,
            Base64UploadAdapter,
            Table,
            TableToolbar,
            Undo,
          ],
          toolbar: [
            'undo', 'redo', '|',
            'heading', '|',
            'bold', 'italic', 'underline', '|',
            'link', 'blockQuote', 'insertTable', 'uploadImage', '|',
            'bulletedList', 'numberedList', '|',
            'outdent', 'indent',
          ],
          image: {
            toolbar: ['imageStyle:inline', 'imageStyle:block', 'imageStyle:side', '|', 'toggleImageCaption'],
          },
          table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
          },
          placeholder,
        }}
        onChange={(_event, editor) => {
          onChange(editor.getData());
        }}
      />
    </div>
  );
};

export default RichTextEditor;
